import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import { TRASH_RETENTION_DAYS, type TrashedPhoto } from '@photos/shared';
import { Link } from 'react-router';
import { useTrash, useTrashAction, useTrashPhoto } from '../../api/hooks';
import { Hero } from '../../components/Hero';
import { Icon } from '../../components/Icon';
import { LightboxFrame } from '../../components/LightboxFrame';
import { Modal } from '../../components/Modal';
import { PhotoGrid } from '../../components/PhotoGrid';
import { SelectionBar } from '../../components/SelectionBar';
import { useEscape } from '../../hooks/useEscape';
import { fmtCount, fmtLongDateTime, fmtShortDate, wallClock } from '../../lib/format';
import { applySelectionClick, toggleGroup } from '../../lib/selection';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { PhotoDetails } from './StudioLightbox';
import { useUndoToast } from './studioUi';

const photosN = (n: number) => `${fmtCount(n)} ${n === 1 ? 'photo' : 'photos'}`;

/** "Delete permanently": typing DELETE confirms, since it can't be undone. */
function DeleteDialog({ n, busy, onConfirm, onClose }: { n: number; busy: boolean; onConfirm(): void; onClose(): void }) {
  const [typed, setTyped] = useState('');
  return (
    <Modal onClose={onClose} labelledBy="perm-del-title">
      <h3 id="perm-del-title">Delete {photosN(n)} permanently?</h3>
      <p className="help">
        The originals and every size are deleted for good. This can’t be undone, not even from Activity. Type <b>DELETE</b> to confirm.
      </p>
      <div className="field">
        <input className="inp" aria-label="Type DELETE to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary danger-fill" disabled={typed !== 'DELETE' || busy} onClick={onConfirm}>
          Delete permanently
        </button>
      </div>
    </Modal>
  );
}

/** Read-only viewer for a photo in Trash: when and by whom it was trashed, its albums and details; Restore, Delete. */
function TrashViewer({ photos, index, onIndex, onClose, onRestore, onDelete, busy }: {
  photos: TrashedPhoto[];
  index: number;
  onIndex(i: number): void;
  onClose(): void;
  onRestore(id: string): void;
  onDelete(id: string): void;
  busy: boolean;
}) {
  const { access, tagIndex } = useSession();
  const p = photos[index];
  const detail = useTrashPhoto(p?.id);
  const [confirming, setConfirming] = useState(false);
  if (!p) return <LightboxFrame photos={photos} index={index} title="" onIndex={onIndex} onClose={onClose}>{null}</LightboxFrame>;
  const d = detail.data;
  const title = tagIndex.eventLabel(p.tags) || p.fileName;
  return (
    <LightboxFrame photos={photos} index={index} title={title} onIndex={onIndex} onClose={onClose} paused={confirming}>
      <h3>{title}</h3>
      <div className="sub">{fmtLongDateTime(wallClock(p.takenAt))}</div>
      <div className="sec">
        <div className="lbl">In Trash</div>
        <p style={{ margin: 0 }}>
          Since {fmtShortDate(new Date(p.trashedAt))}
          {p.trashedByName ? ` · by ${p.trashedByName}` : ''}
        </p>
        <small style={{ color: 'var(--muted)' }}>Deleted permanently after {TRASH_RETENTION_DAYS} days.</small>
      </div>
      <div className="sec">
        <div className="lbl">Albums</div>
        <div className="albumlist">
          {(d?.albums ?? []).map((a) => (
            <div key={a.id} className="albumrow">
              <Icon name="folder" />
              <Link to={`/studio/albums/${a.id}`} onClick={onClose}>
                {a.name}
              </Link>
              {a.home && <span className="pill">Home</span>}
            </div>
          ))}
          {d && !d.albums.length && <span style={{ color: 'var(--muted)' }}>—</span>}
        </div>
      </div>
      <PhotoDetails p={p} d={d} />
      <div className="sec">
        <div className="acts">
          <button type="button" className="btn primary" onClick={() => onRestore(p.id)} disabled={busy}>
            <Icon name="undo" />
            Restore
          </button>
          {access.isAdmin && (
            <button type="button" className="btn danger" onClick={() => setConfirming(true)} disabled={busy}>
              <Icon name="trash" />
              Delete permanently
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <DeleteDialog
          n={1}
          busy={busy}
          onConfirm={() => {
            setConfirming(false);
            onDelete(p.id);
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </LightboxFrame>
  );
}

/** `/studio/trash` (media): restore photos; admins can delete them permanently. */
export function TrashPage() {
  const { access } = useSession();
  const trash = useTrash();
  const action = useTrashAction();
  const toast = useToast();
  const undoToast = useUndoToast();
  const items = useMemo(() => trash.data ?? [], [trash.data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [viewing, setViewing] = useState(-1);
  const last = useRef<string | null>(null);
  const clear = useCallback(() => setSelected(new Set()), []);
  useEscape(clear, selected.size > 0 && !confirming && viewing < 0);
  const ids = items.map((p) => p.id);
  const selectedPhotos = items.filter((p) => selected.has(p.id));

  // Like the other grids: a click opens the viewer; the checkbox, modifier keys or an active selection select.
  const onTileClick = (e: MouseEvent, id: string, viaCheck: boolean) => {
    if (viaCheck || selected.size || e.shiftKey || e.metaKey || e.ctrlKey) {
      setSelected((s) => applySelectionClick(s, ids, id, last.current, e.shiftKey));
      last.current = id;
      return;
    }
    setViewing(ids.indexOf(id));
  };

  const restore = (list: string[]) =>
    action.mutate(
      { action: 'restore', ids: list },
      {
        onSuccess: (r) => {
          undoToast({ icon: 'check', title: `Restored ${photosN(r.changed)}`, sub: 'They’re back in their albums.' }, r.activityId);
          clear();
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t restore', sub: e.message }),
      },
    );

  const remove = (list: string[]) =>
    action.mutate(
      { action: 'delete', ids: list },
      {
        onSuccess: (r) => {
          setConfirming(false);
          toast({ icon: 'trash', title: `Deleted ${photosN(r.changed)} permanently` });
          clear();
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t delete', sub: e.message }),
      },
    );

  return (
    <>
      <Hero
        title="Trash"
        sub={trash.data ? [photosN(items.length)] : []}
        coverUrl={items[0]?.thumbUrl}
        layoutKey={`trash|${items.length}`}
        actions={
          items.length > 0 ? (
            <button type="button" className="btn sm" onClick={() => setSelected(new Set(ids))}>
              <Icon name="check" />
              Select all
            </button>
          ) : undefined
        }
      />
      <div className="notice">
        <Icon name="trash" />
        <span>Photos in Trash are deleted permanently after {TRASH_RETENTION_DAYS} days.</span>
      </div>
      {trash.isError ? (
        <div className="empty">
          <h3>Couldn’t load the Trash</h3>
          {trash.error.message}
        </div>
      ) : !trash.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : !items.length ? (
        <div className="empty">
          <h3>Trash is empty</h3>
          Photos moved to Trash show up here.
        </div>
      ) : (
        <PhotoGrid
          items={items}
          total={items.length}
          selected={selected}
          source="glean"
          hasMore={false}
          loadMore={() => undefined}
          onTileClick={onTileClick}
          onSelectDay={(dayIds) => setSelected((s) => toggleGroup(s, dayIds))}
        />
      )}
      <SelectionBar photos={selectedPhotos} onClear={clear} share={false} canDownload={false}>
        <button type="button" className="btn primary" onClick={() => restore([...selected])} disabled={action.isPending}>
          <Icon name="undo" />
          Restore
        </button>
        {access.isAdmin && (
          <button type="button" className="btn danger" onClick={() => setConfirming(true)} disabled={action.isPending}>
            <Icon name="trash" />
            Delete permanently
          </button>
        )}
      </SelectionBar>
      {confirming && <DeleteDialog n={selected.size} busy={action.isPending} onConfirm={() => remove([...selected])} onClose={() => setConfirming(false)} />}
      {viewing >= 0 && (
        <TrashViewer
          photos={items}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(-1)}
          onRestore={(id) => restore([id])}
          onDelete={(id) => remove([id])}
          busy={action.isPending}
        />
      )}
    </>
  );
}
